import { Box, Chip, TextField, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { RefObject } from "react";
// import { DEFAULT_FONTSIZE } from "../../utils/utils";
import Flex from "../Flex";
import { inputLabel } from "../MaskInput";
import { MData } from "./types";
import { PRIMITIVE } from "../ds";

interface MultiSelectTriggerProps<T extends MData> {
  anchorRef: RefObject<HTMLDivElement>;
  label?: string;
  required?: boolean;
  placeholder: string;
  name?: string;
  error?: string | boolean;
  disabled?: boolean;
  readOnly?: boolean;
  open: boolean;
  value: T[];
  idKey: keyof T;
  labelKey: keyof T;
  limitTags: number | null;
  onTogglePaper: () => void;
}

export const MultiSelectTrigger = <T extends MData>({
  anchorRef,
  label,
  required,
  placeholder,
  name,
  error,
  disabled,
  readOnly,
  open,
  value,
  idKey,
  labelKey,
  limitTags,
  onTogglePaper,
}: MultiSelectTriggerProps<T>) => {
  const theme = useTheme();

  const triggerContent = (() => {
    if (!value.length) {
      // Nothing selected and nothing to select: a placeholder would read as an
      // invitation to open a dropdown that cannot be opened.
      if (readOnly) return null;
      return (
        <Box
          component="span"
          sx={{
            color: theme.palette.text.disabled,
            fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
            flex: 1,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {placeholder}
        </Box>
      );
    }
    const shown = limitTags === null ? value : value.slice(0, limitTags ?? 1);
    const extra = limitTags === null ? 0 : value.length - shown.length;
    return (
      <Flex alignItems="center" gap={0.5} flex={1} sx={{ overflow: "hidden" }}>
        {shown.map((v, i) => (
          <Chip
            key={String(v[idKey]) + i}
            size="small"
            label={String(v[labelKey] ?? "")}
            sx={{ height: 21, fontSize: "13px", minWidth: 0 }}
          />
        ))}
        {extra > 0 && (
          <Box
            component="span"
            sx={{ fontSize: "13px", color: theme.palette.text.secondary }}
          >
            {`+${extra}`}
          </Box>
        )}
      </Flex>
    );
  })();

  return (
    <Box ref={anchorRef} sx={{ width: "100%" }}>
      <TextField
        sx={
          open
            ? {
                // Force the hover border colour even though MUI doesn't ship a hovered prop
                "&& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.text.primary,
                },
              }
            : undefined
        }
        fullWidth
        size="small"
        name={name}
        label={inputLabel(label, required)}
        error={!!error}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        onClick={readOnly ? undefined : onTogglePaper}
        InputProps={{
          readOnly: true,
          inputComponent: "div" as any,
          inputProps: {
            children: triggerContent,
            // The trigger is a div, not a real input, so the theme's read-only
            // styling keys off this marker instead of the `readonly` attribute.
            "data-readonly": readOnly ? "true" : undefined,
            style: {
              display: "flex",
              alignItems: "center",
              minHeight: 24,
              cursor: disabled || readOnly ? "default" : "pointer",
            },
            onClick: readOnly ? undefined : onTogglePaper,
          },
          // No popup to open, so the chevron would be an affordance that does nothing.
          endAdornment: readOnly ? null : open ? (
            <KeyboardArrowUpIcon
              fontSize="small"
              sx={{ color: theme.palette.text.secondary, mr: 0.5 }}
            />
          ) : (
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{ color: theme.palette.text.secondary, mr: 0.5 }}
            />
          ),
          // sx: {
          //   cursor: disabled || readOnly ? "default" : "pointer",
          //   "& .MuiInputBase-input": { ...DEFAULT_FONTSIZE, py: 1 },
          // },
        }}
      />
    </Box>
  );
};
